import uniqBy from 'lodash/uniqBy';

import {
  Project,
  ProjectsAvailabilityByProjectKey,
  RecentAndMoreProjects,
} from '@qality/common-model';
import { getIsProjectAvailablePredicate } from '@qality/common-service/src/SettingsService';

import API from './API';
import SettingServiceUnique from './SettingServiceUnique';

// 50 is max value supported by jira cloud API
const MAX_RESULTS = 50;

type ProjectSearchParams = {
  query: string;
  limit: number;
  filterByQAlitySettings: boolean;
  requestOnce: boolean;
  projectsAvailabilityByProjectKey: ProjectsAvailabilityByProjectKey;
};

let _projectsAvailabilityByProjectKeyCache = null;

async function getCachedProjectsAvailability(
  filterByQAlitySettings,
  projectsAvailabilityByProjectKey
) {
  if (!_projectsAvailabilityByProjectKeyCache) {
    _projectsAvailabilityByProjectKeyCache = await getSettings(
      filterByQAlitySettings,
      projectsAvailabilityByProjectKey
    );
  }
  return _projectsAvailabilityByProjectKeyCache;
}

export default {
  /**
   * fetch non-private (browse permission) projects with limit
   * @param {*} params
   * @param {string} params.query optional param to filter by literal query
   * @param {number} params.limit can't be greater than 50
   * @param {boolean} filterByQAlitySettings whether to filter availability settings
   * @param {boolean} requestOnce use this to perform only one request (CAUTION: 0 projects can be returned even if there are some matching projects)
   * @returns {Array} list of projects
   */
  async getProjectsPaginated({
    query,
    limit,
    filterByQAlitySettings,
    requestOnce,
    projectsAvailabilityByProjectKey,
  }: ProjectSearchParams): Promise<Array<Project>> {
    if (!limit) {
      return [];
    }

    const _projectsAvailabilityByProjectKey =
      await getCachedProjectsAvailability(
        filterByQAlitySettings,
        projectsAvailabilityByProjectKey
      );

    if (filterByQAlitySettings) {
      if (
        Object.values(_projectsAvailabilityByProjectKey).every(
          (project) => !project.isPluginEnabled
        )
      ) {
        return [];
      }
    }

    const prefixUrl = prepareUrl(query);

    return getProjectsLimited(
      limit,
      prefixUrl,
      requestOnce,
      _projectsAvailabilityByProjectKey
    );
  },

  async getProjects(searchUrl: string): Promise<Array<Project>> {
    return API.request(`${searchUrl}&maxResults=0`)
      .then((response) => {
        const totalNumerOfProjects = JSON.parse(response.body).total;
        const numberOfNeededLoops = Math.ceil(
          totalNumerOfProjects / MAX_RESULTS
        );

        const promises = [];
        for (let i = 0; i < numberOfNeededLoops; i++) {
          promises.push(
            API.request(
              `${searchUrl}&startAt=${
                i * MAX_RESULTS
              }&maxResults=${MAX_RESULTS}`
            )
          );
        }
        return Promise.all(promises);
      })
      .then((pagesOfProjects) => {
        const projects = [];
        pagesOfProjects.forEach((pageOfProjects) => {
          const page = JSON.parse(pageOfProjects.body);
          page.values.forEach((value) => {
            projects.push(new Project(value));
          });
        });
        return projects;
      });
  },

  async getRecentProjects(): Promise<Array<Project>> {
    const url = `/rest/api/3/project/recent`;
    const response = await API.request(url);
    const recentProjects = JSON.parse(response.body);

    return recentProjects.filter((project) => !project.isPrivate);
  },

  async getRecentAndMoreProjects(
    moreProjectsParams: {
      query: string;
      filterByQAlitySettings: boolean;
      projectsAvailabilityByProjectKey: ProjectsAvailabilityByProjectKey;
    },
    numberOfRecentProject = 3,
    numberOfMoreProjects = 7
  ): Promise<RecentAndMoreProjects> {
    // If user uses the search input, we don't need recent projects
    if (moreProjectsParams.query?.length > 0) {
      const moreProjects = await this.getProjectsPaginated({
        ...moreProjectsParams,
        limit: 50,
      });

      return {
        moreProjects,
      };
    }

    // Otherwise both recent and more projects are displayed
    const [recentProjects, moreProjects] = await Promise.all([
      this.getRecentProjects(),
      this.getProjectsPaginated({
        ...moreProjectsParams,
        limit: numberOfMoreProjects + numberOfRecentProject,
      }),
    ]);

    const mostRecentProjectsCropped = recentProjects.slice(
      0,
      Math.max(0, numberOfRecentProject)
    );

    // Filter out 'recent projects' from 'more projects'
    const moreProjectsWithoutRecent = moreProjects
      .filter((project) => {
        return !mostRecentProjectsCropped.find((recentProject) => {
          return recentProject.id === project.id;
        });
      })
      .slice(0, Math.max(0, numberOfMoreProjects));

    return {
      recentProjects: mostRecentProjectsCropped,
      moreProjects: moreProjectsWithoutRecent,
    };
  },

  async isQAlityConfiguredInAnyProject(): Promise<boolean> {
    const url = `/rest/api/2/project/search?expand=issueTypes`;

    const projects = await this.getProjects(url);
    return projects.some((project) =>
      new Project(project).doesGivenProjectHaveQAlityTest()
    );
  },

  async getAnyProjectWithConfiguredQAlity(): Promise<Project> {
    const url = `/rest/api/2/project/search?expand=issueTypes`;

    const projects = await this.getProjects(url);

    const foundProject = projects.find((project) => {
      const projectTmp = new Project(project);
      return projectTmp.doesGivenProjectHaveQAlityTest();
    });

    if (foundProject) {
      return foundProject;
    }
    return null;
  },
};

function prepareUrl(query: string): string {
  let prefixUrl = `/rest/api/2/project/search?maxResults=${MAX_RESULTS}&orderBy=name&action=browse`;
  if (query) {
    prefixUrl = `${prefixUrl}&query=${encodeURIComponent(query)}`;
  }
  return prefixUrl;
}

async function getProjectsLimited(
  limit: number,
  prefixUrl: string,
  requestOnce: boolean,
  projectAvailabilityByProjectKey: ProjectsAvailabilityByProjectKey
): Promise<Array<Project>> {
  let allProjects: Array<Project> = [];
  let isLast = false;
  let startAt = 0;

  while (!isLast && limit > allProjects.length) {
    let batchOfProjects;
    ({ batchOfProjects, isLast } = await getBatchOfProjects(
      prefixUrl,
      startAt,
      requestOnce,
      projectAvailabilityByProjectKey
    ));
    allProjects = allProjects.concat(
      batchOfProjects.filter((project) => !project.isPrivate)
    );
    allProjects = uniqBy(allProjects, (project) => project.id);
    startAt += MAX_RESULTS;
  }
  return allProjects.slice(0, limit);
}

async function getBatchOfProjects(
  prefixUrl: string,
  startAt: number,
  requestOnce: boolean,
  projectAvailabilityByProjectKey: ProjectsAvailabilityByProjectKey
): Promise<{
  batchOfProjects: Array<Project>;
  isLast: boolean;
}> {
  const response = await API.request(`${prefixUrl}&startAt=${startAt}`);
  const jiraProjects = JSON.parse(response.body);
  const isLast = requestOnce || jiraProjects.isLast;
  let batchOfProjects;
  if (projectAvailabilityByProjectKey) {
    batchOfProjects = filterByAvailabilityInProject(
      jiraProjects,
      projectAvailabilityByProjectKey
    );
  } else {
    batchOfProjects = jiraProjects.values;
  }
  return { batchOfProjects, isLast };
}

function filterByAvailabilityInProject(
  jiraProjects: {
    isLast: boolean;
    maxResults: number;
    self: string;
    startAt: number;
    total: number;
    values: Array<Project>;
  },
  settings: ProjectsAvailabilityByProjectKey
) {
  return jiraProjects.values.filter(getIsProjectAvailablePredicate(settings));
}

async function getSettings(
  filterByQAlitySettings: boolean,
  projectsAvailabilityByProjectKey: ProjectsAvailabilityByProjectKey
): Promise<ProjectsAvailabilityByProjectKey> {
  let _projectAvailabilityByProjectKey = null;

  if (filterByQAlitySettings) {
    if (projectsAvailabilityByProjectKey) {
      _projectAvailabilityByProjectKey = projectsAvailabilityByProjectKey;
    } else {
      _projectAvailabilityByProjectKey =
        await SettingServiceUnique.getProjectsAvailabilityByProjectKey();
    }
  }
  return _projectAvailabilityByProjectKey;
}
